import { auth } from '@/api/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

function Login() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const authErrorMessages = {
    'auth/invalid-email': 'Email manzil noto‘g‘ri.',
    'auth/user-disabled': 'Ushbu hisob bloklangan.',
    'auth/user-not-found': 'Bunday foydalanuvchi topilmadi.',
    'auth/wrong-password': 'Parol noto‘g‘ri.',
    'auth/invalid-login-credentials': 'Email yoki parol xato.',
    'auth/too-many-requests':
      'Juda ko‘p urinishlar qilindi, keyinroq urinib ko‘ring.',
    'auth/network-request-failed': 'Internet ulanishingizni tekshiring.',
    'auth/operation-not-allowed': 'Email orqali kirish yoqilmagan.',
  };

  const onSubmit = async (data) => {
    try {
      await signInWithEmailAndPassword(auth, data.email, data.password);
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);

      const message =
        authErrorMessages[error.code] ||
        'Noma’lum xato yuz berdi. Iltimos, qayta urinib ko‘ring.';

      toast({
        title: 'Kirishda xatolik',
        description: message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="w-full xl:grid xl:grid-cols-3 h-screen overflow-hidden">
      <div className="relative flex items-center justify-center h-screen xl:h-auto xl:py-12">
        {/* Logo */}
        <div className="absolute top-4 lg:top-8 mx-auto grid w-[350px] cursor-pointer hover:opacity-90 transition-opacity duration-300">
          <img
            className="w-20 lg:w-28 block dark:hidden"
            src="/assets/logos/studypro-ceo-black.svg"
            alt="Light mode logo"
          />
          <img
            className="w-20 lg:w-28 hidden dark:block"
            src="/assets/logos/studypro-ceo-white.svg"
            alt="Dark mode logo"
          />
        </div>

        {/* Form */}
        <div className="mx-auto grid w-[350px] gap-6">
          <div className="grid gap-2 text-center">
            <h1 className="text-2xl font-bold">Login</h1>
            <p className="text-sm text-muted-foreground">
              Enter your email below to login to your account
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
            {/* Email */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="email">Email</Label>
                {errors.email && (
                  <p className="text-red-500 text-xs">{errors.email.message}</p>
                )}
              </div>
              <Input
                id="email"
                type="email"
                placeholder="email@gmail.com"
                {...register('email', { required: 'Email is required' })}
              />
            </div>

            {/* Password */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {errors.password && (
                  <p className="text-red-500 text-xs">
                    {errors.password.message}
                  </p>
                )}
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="********"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button className="w-full" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-4 text-center text-sm">
            Hisob mavjud emasmi?{' '}
            <Link to="/create-account" className="underline">
              Ro'yxatdan o'tish
            </Link>
          </div>
        </div>
      </div>

      {/* Right side image */}
      <div className="hidden bg-muted xl:block col-span-2">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/crm-system-4fefe.appspot.com/o/cover%2Fcrm-cover.png?alt=media&token=611e8787-6118-454e-aa02-1372d844fe7e"
          alt="Image"
          className="h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
        />
      </div>
    </div>
  );
}

export default Login;
